import aiChatbotApiService from './api';
import type {
  AiConversationRecord,
  AiMessageRecord,
  DirectusItemResponse,
  DirectusItemsResponse
} from '../types/aiHistory';

const AI_CONVERSATION_COLLECTION = 'ai_conversation';
const AI_MESSAGE_COLLECTION = 'ai_message';

export interface CreateConversationInput {
  readonly businessType?: string;
  readonly businessId?: string | number;
  readonly difyCapabilityId?: number | null;
  readonly status?: string;
}

export interface CreateMessageInput {
  readonly conversationId: number;
  readonly role: 'user' | 'assistant';
  readonly content: string;
}

export interface ListConversationsInput {
  readonly businessType?: string;
  readonly businessId?: string | number | null;
  readonly limit?: number;
  readonly offset?: number;
}

const normalizeBusinessId = (businessId: string | number | null ): any => {
  return businessId;
};

export const conversationHistoryService = {
  /**
   * 查询会话列表（支持 businessType 过滤）
   */
  async listConversations(input: ListConversationsInput): Promise<readonly AiConversationRecord[]> {
    const { businessType, businessId, limit = 20, offset = 0 } = input;

    try {
      const res = await aiChatbotApiService.get<DirectusItemsResponse<AiConversationRecord>>(
        `/items/${AI_CONVERSATION_COLLECTION}`,
        {
          params: {
            sort: '-date_updated,-date_created',
            limit,
            filter: {
              user_created: { _eq: sessionStorage.agimx_user_id },
              business_type: { _eq: businessType },
              // business_id: { _eq: normalizeBusinessId(businessId) }
            }
          }
        }
      );
      return res.data ?? [];
    } catch (error) {
      console.error('[conversationHistoryService.listConversations] failed', {
        businessType,
        businessId,
        error
      });
      throw error;
    }
  },

  /**
   * 创建会话（保存 businessType）
   */
  async createConversation(input: CreateConversationInput): Promise<AiConversationRecord> {
    const { businessType, businessId, difyCapabilityId, status = 'active' } = input;
    try {
      const res = await aiChatbotApiService.post<DirectusItemResponse<AiConversationRecord>>(
        `/items/${AI_CONVERSATION_COLLECTION}`,
        {
          business_type: businessType,
        //   business_id: input.businessId,
          dify_capability_id: difyCapabilityId ?? null,
          status: status ?? 'active'
        }
      );
      return res.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[conversationHistoryService.createConversation] failed', {
        input,
        error
      });
      throw error;
    }
  },

  async getMessages(params: {
    readonly conversationId: number;
    readonly limit?: number;
  }): Promise<readonly AiMessageRecord[]> {
    const { conversationId, limit = 200 } = params;

    try {
      const res = await aiChatbotApiService.get<DirectusItemsResponse<AiMessageRecord>>(
        `/items/${AI_MESSAGE_COLLECTION}`,
        {
          params: {
            sort: 'date_created',
            limit,
            filter: {
              conversation_id: { _eq: conversationId }
            }
          }
        }
      );
      return res.data ?? [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[conversationHistoryService.getMessages] failed', {
        conversationId,
        error
      });
      throw error;
    }
  },

  async createMessage(input: CreateMessageInput): Promise<AiMessageRecord> {
    try {
      const res = await aiChatbotApiService.post<DirectusItemResponse<AiMessageRecord>>(
        `/items/${AI_MESSAGE_COLLECTION}`,
        {
          conversation_id: input.conversationId,
          role: input.role,
          content: input.content,
          read_at: null
        }
      );
      return res.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[conversationHistoryService.createMessage] failed', {
        input,
        error
      });
      throw error;
    }
  },

  /** 删除会话 */
  async deleteConversation(id: number): Promise<void> {
    try {
      await aiChatbotApiService.delete(`/items/${AI_CONVERSATION_COLLECTION}/${id}`);
    } catch (error) {
      console.error('[conversationHistoryService.deleteConversation] failed', { id, error });
      throw error;
    }
  },

  /**
   * 批量查询各会话最新一条消息的创建时间
   * 用于正确排序历史会话列表（避免 date_updated 被 is_active 等字段污染）
   * @returns Record<conversationId, latestMessageDateCreated>
   */
  async getLatestMessageTimes(convIds: readonly number[]): Promise<Readonly<Record<number, string>>> {
    if (convIds.length === 0) return {};
    try {
      type AggRow = { conversation_id: number; max: { date_created: string | null } };
      const res = await aiChatbotApiService.get<DirectusItemsResponse<AggRow>>(
        `/items/${AI_MESSAGE_COLLECTION}`,
        {
          params: {
            'aggregate[max][]': 'date_created',
            'groupBy[]': 'conversation_id',
            filter: {
              conversation_id: { _in: convIds }
            },
            limit: -1
          }
        }
      );
      const result: Record<number, string> = {};
      for (const row of res.data ?? []) {
        if (row.max?.date_created) {
          result[row.conversation_id] = row.max.date_created;
        }
      }
      return result;
    } catch (error) {
      console.error('[conversationHistoryService.getLatestMessageTimes] failed', { error });
      return {};
    }
  },

  /** 更新会话标题或其他字段 */
  async updateConversation(id: number, data: Partial<AiConversationRecord>): Promise<void> {
    try {
      await aiChatbotApiService.patch(`/items/${AI_CONVERSATION_COLLECTION}/${id}`, data);
    } catch (error) {
      console.error('[conversationHistoryService.updateConversation] failed', { id, error });
      throw error;
    }
  },

  /**
   * 查询当前用户在指定 businessType 下的激活对话
   * 用于页面重进时恢复上次会话
   */
  async getActiveConversation(businessType: string): Promise<AiConversationRecord | null> {
    try {
      const res = await aiChatbotApiService.get<DirectusItemsResponse<AiConversationRecord>>(
        `/items/${AI_CONVERSATION_COLLECTION}`,
        {
          params: {
            sort: '-date_updated',
            limit: 1,
            filter: {
              user_created: { _eq: sessionStorage.agimx_user_id },
              business_type: { _eq: businessType },
              is_active: { _eq: true }
            }
          }
        }
      );
      return res.data?.[0] ?? null;
    } catch (error) {
      console.error('[conversationHistoryService.getActiveConversation] failed', {
        businessType,
        error
      });
      return null;
    }
  },

  /**
   * 将同一 businessType 下所有激活对话清除，再激活指定对话
   * 保证同时只有一条 is_active=true 的记录
   */
  async setActiveConversation(
    newActiveId: number,
    businessType: string
  ): Promise<void> {
    try {
      // 1. 找出当前 active 的对话（排除目标对话本身）
      const res = await aiChatbotApiService.get<DirectusItemsResponse<AiConversationRecord>>(
        `/items/${AI_CONVERSATION_COLLECTION}`,
        {
          params: {
            fields: 'id',
            filter: {
              user_created: { _eq: sessionStorage.agimx_user_id },
              business_type: { _eq: businessType },
              is_active: { _eq: true },
              id: { _neq: newActiveId }
            }
          }
        }
      );
      // 2. 批量清除旧的 active
      const oldIds = (res.data ?? []).map(c => c.id);
      await Promise.all(
        oldIds.map(id =>
          aiChatbotApiService.patch(`/items/${AI_CONVERSATION_COLLECTION}/${id}`, { is_active: false })
        )
      );
      // 3. 激活目标对话
      await aiChatbotApiService.patch(`/items/${AI_CONVERSATION_COLLECTION}/${newActiveId}`, { is_active: true });
    } catch (error) {
      console.error('[conversationHistoryService.setActiveConversation] failed', {
        newActiveId,
        businessType,
        error
      });
    }
  },

  /**
   * 清除指定 businessType 下所有激活对话（回到欢迎页时使用）
   */
  async clearActiveConversation(businessType: string): Promise<void> {
    try {
      const res = await aiChatbotApiService.get<DirectusItemsResponse<AiConversationRecord>>(
        `/items/${AI_CONVERSATION_COLLECTION}`,
        {
          params: {
            fields: 'id',
            filter: {
              user_created: { _eq: sessionStorage.agimx_user_id },
              business_type: { _eq: businessType },
              is_active: { _eq: true }
            }
          }
        }
      );
      const ids = (res.data ?? []).map(c => c.id);
      await Promise.all(
        ids.map(id =>
          aiChatbotApiService.patch(`/items/${AI_CONVERSATION_COLLECTION}/${id}`, { is_active: false })
        )
      );
    } catch (error) {
      console.error('[conversationHistoryService.clearActiveConversation] failed', {
        businessType,
        error
      });
    }
  }
};